import { useMemo } from 'react';
import { toDateKey, weekdayKeyOf } from './dateUtils';

export function computeEffectiveSlots(weeklySlots, slotOverrides, dateKey, weekdayKey) {
  const override = (slotOverrides || []).find(o => o.date === dateKey);
  if (override) {
    const slots = override.slots || [];
    return { slots, isOverridden: true, isClosed: slots.length === 0 };
  }
  const templateSlots = (weeklySlots && weeklySlots[weekdayKey]) || [];
  return { slots: templateSlots, isOverridden: false, isClosed: false };
}

// Total/remaining are derived from each slot's capacity. `remainingCapacity` is not
// populated by any backend today (there is no booking model yet), so it defaults to
// the full capacity and every open slot reads as 100% available. Once bookings exist
// and start setting `remainingCapacity` on a slot, this starts reflecting it with no
// further changes needed here.
export function computeSlotAvailability(effective) {
  const activeSlots = (effective.slots || []).filter(s => s.isActive !== false);
  const total = activeSlots.reduce((sum, s) => sum + (Number(s.capacity) || 0), 0);
  const remaining = activeSlots.reduce((sum, s) => {
    const capacity = Number(s.capacity) || 0;
    const slotRemaining = s.remainingCapacity != null ? Number(s.remainingCapacity) : capacity;
    return sum + Math.max(0, Math.min(slotRemaining, capacity));
  }, 0);
  return { total, remaining, percentage: total > 0 ? remaining / total : 0 };
}

// Groups a flat booking list into per-slot counts for one date, so the
// calendar can attach remainingCapacity without a chatty per-date API call.
export function bookingCountsForDate(bookings, dateKey) {
  const counts = {};
  for (const b of bookings || []) {
    if (b.date !== dateKey || b.status === 'cancelled') continue;
    const key = `${b.startTime}-${b.endTime}`;
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

// Returns a new slot array with remainingCapacity attached per slot, using
// the counts from bookingCountsForDate. computeSlotAvailability/statusColors
// already read remainingCapacity when present — this is the only place that
// needs to know about bookings.
export function withRemainingCapacity(slots, counts) {
  return (slots || []).map(s => {
    const key = `${s.startTime}-${s.endTime}`;
    const booked = counts[key] || 0;
    return { ...s, remainingCapacity: Math.max(0, (Number(s.capacity) || 0) - booked) };
  });
}

// Single source of truth for calendar day colors, also rendered as the legend.
// bg values reference the theme's CSS custom properties (index.css) so cells
// stay correct under both light and dark mode without any JS theme check.
export const STATUS_LEGEND = [
  { key: 'closed', label: 'Closed', bg: 'var(--badge-red-bg)', dot: '#EA4335' },
  { key: 'overridden', label: 'Custom hours', bg: 'var(--badge-purple-bg)', dot: '#9334e6' },
  { key: 'available', label: '50%+ slots open', bg: 'var(--badge-green-bg)', dot: '#34A853' },
  { key: 'limited', label: '<50% slots open', bg: 'var(--badge-amber-bg)', dot: '#f29900' },
  { key: 'none', label: 'No slots', bg: 'var(--color-surface-muted)', dot: '#9aa0a6' },
];

export function statusColors(effective, isPast) {
  if (isPast) return { bg: '#f8fafc' };
  if (effective.isClosed) return STATUS_LEGEND[0];
  if (effective.isOverridden) return STATUS_LEGEND[1];
  const { total, percentage } = computeSlotAvailability(effective);
  if (total === 0) return STATUS_LEGEND[4];
  return percentage >= 0.5 ? STATUS_LEGEND[2] : STATUS_LEGEND[3];
}

export default function useEffectiveSlots(weeklySlots, slotOverrides, date) {
  return useMemo(() => {
    const dateKey = toDateKey(date);
    const weekdayKey = weekdayKeyOf(date);
    return computeEffectiveSlots(weeklySlots, slotOverrides, dateKey, weekdayKey);
  }, [weeklySlots, slotOverrides, date]);
}
